import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AppLauncher } from '@capacitor/app-launcher';
import { Capacitor } from '@capacitor/core';
import { BackgroundColor, InAppBrowser } from '@capgo/inappbrowser';
import { ActionSheetController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { trim } from 'lodash';

import { APP_CONFIG, AppConfig } from '@smartcharge/shared/index';

import { AuthView, RegisterConfig } from '../auth.component';
import { UserService } from '../../../services';
import { ToastService } from '../../../shared/services/toast/toast.service';
import { AddPaymentComponent } from '../../../components/add-payment/add-payment.component';

@Component({
  selector: 'sc-register',
  standalone: false,
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegisterComponent {
  readonly form = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.email]),
    acceptTerms: new FormControl(false, [Validators.requiredTrue])
  });

  readonly codeControl = new FormControl('', [Validators.required, Validators.pattern(/^\d{6}$/)]);

  readonly config = input<Partial<RegisterConfig>>();

  readonly codeSent = signal<boolean>(false);
  readonly saving = signal<boolean>(false);

  readonly close = output<boolean>();
  readonly switchView = output<AuthView>();

  private readonly actionSheetController = inject(ActionSheetController);
  private readonly appConfig: AppConfig = inject(APP_CONFIG);
  private readonly modalCtrl = inject(ModalController);
  private readonly route = inject(ActivatedRoute);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly userService = inject(UserService);

  readonly obfuscatedEmail = computed(() => {
    const { email } = this.form.value;

    if (!email) {
      return '';
    }

    return `${email.replace(/(.{2})(.*)(?=@)/, (_, start, middle) => {
      return start + '*'.repeat(middle.length);
    })}`;
  });

  constructor() {
    // At this point we want to clean any user data from adhoc sessions
    this.userService.clearUser();

    this.codeControl.valueChanges.subscribe((value) => {
      if (this.codeControl.valid) {
        this.verify();
      }
    });
  }

  register() {
    if (this.form.invalid) {
      return;
    }

    const { firstName, lastName, email } = this.form.value;

    const payload = {
      FirstName: trim(firstName!),
      MiddleName: '',
      LastName: trim(lastName!),
      Email: email!.toLowerCase(),
      Language: this.translate.getCurrentLang() || this.appConfig.defaultLang,
      TimezoneID: Intl.DateTimeFormat().resolvedOptions().timeZone
    };

    const validationKey = this.route.root.snapshot.firstChild?.paramMap.get('validationKEY');

    if (validationKey) {
      Object.assign(payload, { ValidationToken: validationKey });
    }

    this.saving.set(true);
    this.userService
      .completeRegister(payload, this.config()?.loginRes)
      .then((response) => {
        if (response.CompleteRegistrationResult === 'UserNeedsToBeMerged') {
          this.codeSent.set(true);
        } else if (response.LoginReturn && response.LoginReturn.DoesUserNeedToAddPaymentMethod) {
          this.close.emit(true);
          this.openAddPaymentMethod(response.LoginReturn.customer);
        } else {
          this.close.emit(true);
        }
      })
      .catch((error) => {
        this.toastService.error(error);
      })
      .finally(() => {
        this.saving.set(false);
      });
  }

  async openAgreements(event?: MouseEvent) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const lang = this.translate.getCurrentLang() || this.appConfig.defaultLang;
    const termsURL = this.appConfig.termsAndConditions[lang] || Object.values(this.appConfig.termsAndConditions)[0];
    const privacyURL = this.appConfig.privacyPolicy[lang] || Object.values(this.appConfig.privacyPolicy)[0];

    const actionSheet = await this.actionSheetController.create({
      buttons: [
        {
          text: this.translate.instant('APP.TERMS'),
          handler: () => {
            this.openUrl(termsURL, this.translate.instant('APP.TERMS'));
          }
        },
        {
          text: this.translate.instant('APP.PRIVACY'),
          handler: () => {
            this.openUrl(privacyURL, this.translate.instant('APP.PRIVACY'));
          }
        },
        {
          text: this.translate.instant('APP.CANCEL'),
          role: 'cancel'
        }
      ]
    });

    actionSheet.present();
  }

  verify(): void {
    const { email } = this.form.value;
    const code = this.codeControl.value;

    if (!email || !code) {
      return;
    }

    this.saving.set(true);

    this.userService
      .mergeAccounts(
        {
          OTPCode: code,
          Email: email
        },
        this.config()?.loginRes
      )
      .then(() => {
        this.close.emit(true);
      })
      .catch((error) => {
        this.toastService.error(error);
      })
      .finally(() => {
        this.saving.set(false);
      });
  }

  private async openAddPaymentMethod(customer: any) {
    const modal = await this.modalCtrl.create({
      component: AddPaymentComponent,
      componentProps: {
        authPage: true,
        customer
      },
      presentingElement: document.querySelector('div.ion-page')
    });
    await modal.present();
  }

  private openUrl(url: string, title: string) {
    if (Capacitor.isNativePlatform()) {
      InAppBrowser.openWebView({
        url,
        title,
        backgroundColor: BackgroundColor.WHITE
      });
    } else {
      AppLauncher.openUrl({ url });
    }
  }
}
